import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Technologies } from '../../services';

const CACHE_DURATION = 5 * 60 * 1000;

const initialState = {
  items: [],
  isFetching: false,
  error: null,
  lastFetched: null,
  repositoryId: null,
};

export const fetchTechnologies = createAsyncThunk(
  'technologies/fetchTechnologies',
  async (repositoryId, { rejectWithValue }) => {
    try {
      const response = await Technologies.fetchAll({ profileId: repositoryId });
      return {
        items: response.data || [],
        repositoryId,
      };
    } catch (error) {
      console.error('Error fetching technologies:', error);
      return rejectWithValue(error.message || 'Failed to fetch technologies');
    }
  }
);

const technologiesSlice = createSlice({
  name: 'technologies',
  initialState,
  reducers: {
    invalidate(state) {
      state.lastFetched = null;
    },
    addTechnology(state, action) {
      state.items.push(action.payload);
    },
    removeTechnology(state, action) {
      const techId = action.payload;
      state.items = state.items.filter((item) => item.tech_id !== techId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTechnologies.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchTechnologies.fulfilled, (state, action) => {
        state.isFetching = false;
        state.items = action.payload.items;
        state.repositoryId = action.payload.repositoryId;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchTechnologies.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { invalidate, addTechnology, removeTechnology } =
  technologiesSlice.actions;

// Thunk wrapper with time-based caching and repository scoping
export const fetchTechnologiesIfNeeded = (repositoryId) =>
  (dispatch, getState) => {
    const state = getState().technologies;

    const repoChanged =
      state.repositoryId && state.repositoryId !== repositoryId;
    const isCacheValid =
      state.items.length > 0 &&
      state.lastFetched &&
      Date.now() - state.lastFetched < CACHE_DURATION &&
      !repoChanged;

    if (isCacheValid || state.isFetching) {
      return;
    }

    dispatch(fetchTechnologies(repositoryId));
  };

export default technologiesSlice.reducer;
